import type { Condition, Annotation as FhirAnnotation } from "@medplum/fhirtypes";
import { CONDITION_ENTITY_ID_SYSTEM, getTRPCClient, OBSERVATION_ENTITY_ID_SYSTEM } from "@repo/api";
import {
  type CreateConditionInput,
  conditionIdSchema,
  createConditionInputSchema,
  observationIdSchema,
  patientIdSchema,
  practitionerIdSchema,
  type UpdateConditionInput,
  updateConditionInputSchema,
  visitIdSchema,
} from "@repo/api/schemas";
import type { ConditionId, ObservationId, PatientId, PractitionerId, VisitId } from "@repo/domain";
import {
  CONDITION_CATEGORY_SYSTEM as CATEGORY_SYSTEM,
  CONDITION_CLINICAL_SYSTEM as CLINICAL_SYSTEM,
  CONDITION_VERIFICATION_SYSTEM as VER_SYSTEM,
} from "@repo/fhir-systems";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { ConditionRow } from "../fhir/types";
import { optimisticPatientReference, patientDomainId } from "./_patient-reference";
import { optimisticPractitionerReference, practitionerDomainId } from "./_practitioner-reference";
import { optimisticVisitExecutionReference, visitDomainId } from "./_visit-reference";
import type { Annotation, CodeableConcept, Period, Ref } from "./shared/types";

/**
 * Condition — normalized read-model.
 * `onset` and `abatement` keep FHIR's polymorphic suffix convention (DateTime / Period / String).
 */
export interface ConditionDto {
  conditionId: ConditionId;
  patientId: PatientId;
  visitId?: VisitId;

  code: CodeableConcept;
  category?: "problem-list-item" | "encounter-diagnosis";

  clinicalStatus?: "active" | "recurrence" | "relapse" | "inactive" | "remission" | "resolved";
  verificationStatus?: "unconfirmed" | "provisional" | "differential" | "confirmed" | "refuted" | "entered-in-error";
  severity?: CodeableConcept;

  onsetDateTime?: string;
  onsetPeriod?: Period;
  onsetString?: string;

  abatementDateTime?: string;
  abatementPeriod?: Period;
  abatementString?: string;

  recorderPractitionerId?: PractitionerId;
  recordedDate?: string;

  notes?: Annotation[];

  /** Observation ids linked as supporting evidence (e.g. symptom manifestations). */
  evidenceObservationIds?: ObservationId[];
}

const CONDITION_COLLECTION = "condition";

const VALID_CLINICAL: ReadonlySet<string> = new Set([
  "active",
  "recurrence",
  "relapse",
  "inactive",
  "remission",
  "resolved",
]);
const VALID_VERIFICATION: ReadonlySet<string> = new Set([
  "unconfirmed",
  "provisional",
  "differential",
  "confirmed",
  "refuted",
  "entered-in-error",
]);
const VALID_CATEGORIES: ReadonlySet<string> = new Set(["problem-list-item", "encounter-diagnosis"]);

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function flatStatusCode(
  c: { coding?: Array<{ system?: string; code?: string }> } | undefined,
  expectedSystem: string,
  valid: ReadonlySet<string>,
): string | undefined {
  const code = c?.coding?.find((co) => co.system === expectedSystem)?.code ?? c?.coding?.[0]?.code;
  return code && valid.has(code) ? code : undefined;
}

function pickCategory(category: Condition["category"]): ConditionDto["category"] | undefined {
  for (const cc of category ?? []) {
    for (const co of cc.coding ?? []) {
      if (co.system === CATEGORY_SYSTEM && co.code && VALID_CATEGORIES.has(co.code)) {
        return co.code as ConditionDto["category"];
      }
    }
  }
  return undefined;
}

function flatPeriod(p: { start?: string; end?: string } | undefined): Period | undefined {
  if (!p) return undefined;
  const out = {
    ...(p.start ? { start: p.start } : {}),
    ...(p.end ? { end: p.end } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function parseAuthorRef(ref: string | undefined): Annotation["authorRef"] | undefined {
  if (!ref) return undefined;
  const [type, id] = ref.split("/");
  const allowed = new Set(["Practitioner", "Patient", "RelatedPerson", "Organization"]);
  if (!type || !id || !allowed.has(type)) return undefined;
  return { resourceType: type, id } as Ref<"Practitioner" | "Patient" | "RelatedPerson" | "Organization">;
}

function normalizeNotes(notes: FhirAnnotation[] | undefined): Annotation[] | undefined {
  if (!notes?.length) return undefined;
  return notes.map((n) => ({
    text: n.text ?? "",
    ...(n.time ? { time: n.time } : {}),
    ...(parseAuthorRef(n.authorReference?.reference)
      ? { authorRef: parseAuthorRef(n.authorReference?.reference) }
      : {}),
  }));
}

function toDto(c: ConditionRow): ConditionDto | undefined {
  if (!c.domainId) return undefined;
  const conditionId = conditionIdSchema.safeParse(c.domainId);
  const patientId = patientIdSchema.safeParse(patientDomainId(c.subject));
  if (!conditionId.success || !patientId.success) return undefined;
  const code = flatCodeable(c.code) ?? {};
  const severity = flatCodeable(c.severity);
  const recorderPractitionerId = practitionerIdSchema.safeParse(practitionerDomainId(c.recorder));
  const visitId = visitIdSchema.safeParse(visitDomainId(c.encounter));

  const evidenceObservationIds = (c.evidence ?? [])
    .flatMap((e) => e.detail ?? [])
    .flatMap((detail) => {
      if (detail.identifier?.system !== OBSERVATION_ENTITY_ID_SYSTEM || !detail.identifier.value) return [];
      const observationId = observationIdSchema.safeParse(detail.identifier.value);
      return observationId.success ? [observationId.data] : [];
    });

  return {
    conditionId: conditionId.data,
    patientId: patientId.data,
    ...(visitId.success ? { visitId: visitId.data } : {}),
    code,
    ...(pickCategory(c.category) ? { category: pickCategory(c.category) } : {}),
    ...(flatStatusCode(c.clinicalStatus, CLINICAL_SYSTEM, VALID_CLINICAL)
      ? {
          clinicalStatus: flatStatusCode(
            c.clinicalStatus,
            CLINICAL_SYSTEM,
            VALID_CLINICAL,
          ) as ConditionDto["clinicalStatus"],
        }
      : {}),
    ...(flatStatusCode(c.verificationStatus, VER_SYSTEM, VALID_VERIFICATION)
      ? {
          verificationStatus: flatStatusCode(
            c.verificationStatus,
            VER_SYSTEM,
            VALID_VERIFICATION,
          ) as ConditionDto["verificationStatus"],
        }
      : {}),
    ...(severity ? { severity } : {}),
    ...(c.onsetDateTime ? { onsetDateTime: c.onsetDateTime } : {}),
    ...(flatPeriod(c.onsetPeriod) ? { onsetPeriod: flatPeriod(c.onsetPeriod) } : {}),
    ...(c.onsetString ? { onsetString: c.onsetString } : {}),
    ...(c.abatementDateTime ? { abatementDateTime: c.abatementDateTime } : {}),
    ...(flatPeriod(c.abatementPeriod) ? { abatementPeriod: flatPeriod(c.abatementPeriod) } : {}),
    ...(c.abatementString ? { abatementString: c.abatementString } : {}),
    ...(recorderPractitionerId.success ? { recorderPractitionerId: recorderPractitionerId.data } : {}),
    ...(c.recordedDate ? { recordedDate: c.recordedDate } : {}),
    ...(normalizeNotes(c.note) ? { notes: normalizeNotes(c.note) } : {}),
    ...(evidenceObservationIds.length ? { evidenceObservationIds } : {}),
  };
}

interface UseConditionsParams {
  visitId?: VisitId;
  patientId?: string;
}

interface UseConditionsResult {
  conditions: ConditionDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useConditions(params?: UseConditionsParams): UseConditionsResult {
  const collection = useCollection(CONDITION_COLLECTION);
  const visitId = params?.visitId;
  const patientId = params?.patientId;

  const result = useLiveQuery({
    queryKey: [collection.id, "conditions", visitId ?? null, patientId ?? null],
    query: (q) => {
      const base = q.from({ c: collection });
      if (visitId) {
        return base
          .where(({ c }) => eq(c.visitId, visitId))
          .fn.select(({ c }) => ({
            dto: toDto(c),
          }));
      }
      if (patientId) {
        return base
          .where(({ c }) => eq(c.subject?.identifier?.value, patientId))
          .fn.select(({ c }) => ({
            dto: toDto(c),
          }));
      }
      return undefined;
    },
  });

  return {
    conditions: ((result.data as Array<{ dto?: ConditionDto }> | undefined) ?? []).flatMap((row) =>
      row.dto ? [row.dto] : [],
    ),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

const conditions = () => getDataStoreClient().getCollection<ConditionRow, string>("condition");

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(conditionId: ConditionId): string {
  if (!conditions().has(conditionId)) throw new Error(`Condition '${conditionId}' is not loaded`);
  return conditionId;
}

/** Intent: "record a diagnosis or problem-list condition" */
export const recordCondition = defineWrite<CreateConditionInput>({
  optimistic: (params) => {
    const input = createConditionInputSchema.parse(params);
    const optimisticConditionId = input.id;
    conditions().insert({
      resourceType: "Condition",
      identifier: [{ system: CONDITION_ENTITY_ID_SYSTEM, value: optimisticConditionId }],
      domainId: optimisticConditionId,
      subject: optimisticPatientReference(input.patient.id),
      encounter: input.visitId ? optimisticVisitExecutionReference(input.visitId) : undefined,
      code: input.code,
      category: [{ coding: [{ system: CATEGORY_SYSTEM, code: input.category }] }],
      clinicalStatus: { coding: [{ system: CLINICAL_SYSTEM, code: input.clinicalStatus }] },
      verificationStatus: { coding: [{ system: VER_SYSTEM, code: input.verificationStatus }] },
      severity: input.severity,
      recorder: input.recorder ? optimisticPractitionerReference(input.recorder.id) : undefined,
      recordedDate: input.recordedDate,
      note: input.note ? [{ text: input.note }] : undefined,
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.condition.record.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "update a condition's status, severity, or note" */
export const updateCondition = defineWrite<UpdateConditionInput>({
  optimistic: (params) => {
    const input = updateConditionInputSchema.parse(params);
    const collection = conditions();
    const resourceId = resourceIdForDomainId(input.conditionId);
    const changes: Partial<Condition> = {};
    if (input.clinicalStatus !== undefined) {
      changes.clinicalStatus = { coding: [{ system: CLINICAL_SYSTEM, code: input.clinicalStatus }] };
    }
    if (input.verificationStatus !== undefined) {
      changes.verificationStatus = { coding: [{ system: VER_SYSTEM, code: input.verificationStatus }] };
    }
    if (input.severity !== undefined) changes.severity = input.severity;
    if (input.onsetDateTime !== undefined) changes.onsetDateTime = input.onsetDateTime;
    if (input.note !== undefined) changes.note = input.note ? [{ text: input.note }] : undefined;

    collection.update(resourceId, (draft) => {
      Object.assign(draft, changes);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.condition.update.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
